import { z } from 'zod';

import { JOB_DISPOSITIONS, JOB_STATUSES, BILLING_STATUSES, BILLING_TYPES, HIGHLIGHT_COLORS, JOB_CARD_STATUSES, JOB_INVOICE_STATUSES } from '../types/job';
import { INVOICE_TYPES } from '../types/invoice';
import { jobCardFieldsSchema } from './jobCardFields';
import { EXPENSE_SORT_FIELDS } from '../utils/expenseSort';

const isoDateTime = () => z.string().datetime({ offset: true });
const isoDate = () => z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD format');

export const jobSchema = z.object({
  id: z.string().uuid(),
  jobCode: z.string().optional().nullable(),
  clientId: z.string().uuid(),
  locationId: z.string().uuid(),
  managerId: z.string().uuid().nullable(),
  projectType: z.string().min(1),
  customProjectType: z.string().optional().nullable(),
  description: z.string().optional().nullable(),
  serviceAddressNote: z.string().optional().nullable(),
  billingAddressLabel: z.string().optional().nullable(),
  billingAddressLine1: z.string().optional().nullable(),
  billingAddressLine2: z.string().optional().nullable(),
  billingCity: z.string().optional().nullable(),
  billingState: z.string().optional().nullable(),
  billingZip: z.string().optional().nullable(),
  status: z.enum(JOB_STATUSES),
  billingStatus: z.enum(BILLING_STATUSES).optional().nullable(),
  jobCardStatus: z.enum(JOB_CARD_STATUSES),
  invoiceStatus: z.enum(JOB_INVOICE_STATUSES),
  invoicePaidDate: isoDate().optional().nullable(),
  disposition: z.enum(JOB_DISPOSITIONS).optional().nullable(),
  noticeDate: isoDate().optional().nullable(),
  estimateSendDate: isoDate().optional().nullable(),
  startDate: isoDate().optional().nullable(),
  completedDate: isoDate().optional().nullable(),
  onedrivePath: z.string().trim().min(1).optional().nullable(),
  jobCardPath: z.string().trim().min(1).optional().nullable(),
  jobCardFields: jobCardFieldsSchema.nullable().optional(),
  invoiceType: z.enum(INVOICE_TYPES).optional().nullable(),
  invoiceNumber: z.string().optional().nullable(),
  billingType: z.enum(BILLING_TYPES).optional().nullable(),
  billingRate: z.string().optional().nullable(),
  lastInvoicedDate: isoDate().optional().nullable(),
  isArchived: z.boolean(),
  highlightColor: z.enum(HIGHLIGHT_COLORS).optional().nullable(),
  deletedAt: isoDateTime().optional().nullable(),
  createdAt: isoDateTime(),
  updatedAt: isoDateTime(),
});

export const jobInputSchema = z.object({
  clientId: z.string().uuid(),
  locationId: z.string().uuid(),
  managerId: z.string().uuid().nullable().optional().or(z.literal('').transform(() => undefined)),
  projectType: z.string().min(1),
  customProjectType: z.string().optional(),
  description: z.string().optional(),
  serviceAddressNote: z.string().optional(),
  billingAddressLabel: z.string().trim().optional(),
  billingAddressLine1: z.string().trim().optional(),
  billingAddressLine2: z.string().trim().optional(),
  billingCity: z.string().trim().optional(),
  billingState: z.string().trim().optional(),
  billingZip: z.string().trim().optional(),
  disposition: z.enum(JOB_DISPOSITIONS).optional(),
  billingStatus: z.enum(BILLING_STATUSES).optional(),
  noticeDate: isoDate().optional(),
  estimateSendDate: isoDate().optional(),
  startDate: isoDate().optional(),
  completedDate: isoDate().optional(),
  onedrivePath: z.string().trim().min(1).optional(),
  jobCardPath: z.string().trim().min(1).optional(),
  invoiceType: z.enum(INVOICE_TYPES).optional(),
  invoiceNumber: z.string().optional(),
  billingType: z.enum(BILLING_TYPES).optional(),
  billingRate: z.string().optional(),
  lastInvoicedDate: isoDate().optional(),
  templateId: z.string().uuid().optional().nullable(),
}).refine((data) => {
  if (data.projectType === 'Other' && !data.customProjectType?.trim()) {
    return false;
  }
  return true;
}, {
  message: 'Custom project type is required when "Other" is selected',
  path: ['customProjectType'],
});

export const jobUpdateSchema = z.object({
  clientId: z.string().uuid().optional(),
  locationId: z.string().uuid().optional(),
  managerId: z.string().uuid().nullable().optional(),
  projectType: z.string().min(1).optional(),
  customProjectType: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  serviceAddressNote: z.string().nullable().optional(),
  billingAddressLabel: z.string().nullable().optional(),
  billingAddressLine1: z.string().nullable().optional(),
  billingAddressLine2: z.string().nullable().optional(),
  billingCity: z.string().nullable().optional(),
  billingState: z.string().nullable().optional(),
  billingZip: z.string().nullable().optional(),
  disposition: z.enum(JOB_DISPOSITIONS).nullable().optional(),
  billingStatus: z.enum(BILLING_STATUSES).nullable().optional(),
  jobCardStatus: z.enum(JOB_CARD_STATUSES).optional(),
  invoiceStatus: z.enum(JOB_INVOICE_STATUSES).optional(),
  invoicePaidDate: isoDate().nullable().optional(),
  noticeDate: isoDate().nullable().optional(),
  estimateSendDate: isoDate().nullable().optional(),
  startDate: isoDate().nullable().optional(),
  completedDate: isoDate().nullable().optional(),
  onedrivePath: z.string().trim().min(1).nullable().optional(),
  jobCardPath: z.string().trim().min(1).nullable().optional(),
  invoiceType: z.enum(INVOICE_TYPES).nullable().optional(),
  invoiceNumber: z.string().nullable().optional(),
  billingType: z.enum(BILLING_TYPES).nullable().optional(),
  billingRate: z.string().nullable().optional(),
  lastInvoicedDate: isoDate().nullable().optional(),
  isArchived: z.boolean().optional(),
  highlightColor: z.enum(HIGHLIGHT_COLORS).nullable().optional(),
  expenseSortField: z.enum(EXPENSE_SORT_FIELDS).nullable().optional(),
  expenseSortDirection: z.enum(['asc', 'desc']).nullable().optional(),
}).refine((data) => {
  // Only validate customProjectType if both fields are being updated together
  if (data.projectType === 'Other' && data.customProjectType !== undefined && !data.customProjectType?.trim()) {
    return false;
  }
  return true;
}, {
  message: 'Custom project type is required when "Other" is selected',
  path: ['customProjectType'],
});
